import React, {
  useEffect, useMemo, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import classes from '../../../pipeline.module.scss';
import Api from '../../../../../../../api/api';
import Chip from '../../../../../../_common/Chip/Chip';
import {
  BarChart, Card, LineChart, SectionHead, StatsCards, Steps,
} from '../../PipelineComponents';
import { formattedCount } from '../../../pipelineHelpers';
import WaitingRxBoardCard from './WaitingRxBoardCard';

const STAGE_LABELS = {
  identified: 'Identified - flagged, no action yet',
  contacted: 'Contacted - member or prescriber reached',
  in_progress: 'In progress - transfer / PA underway',
};

const STAGE_TIPS = {
  identified: {
    text: 'Members flagged but not yet contacted.',
    steps: ['Part of the open pipeline count above.'],
    note: 'The one stage entirely within your control. Anything sitting here more than a day or two is purely'
      + ' a staffing question.',
  },
  contacted: {
    text: 'Members reached once, with nothing agreed yet.',
    steps: ['Still open, so they are counted in the open pipeline above.'],
    note: 'Members stall here more than anywhere else. A follow-up date on every one is what stops them going'
      + ' quiet.',
  },
  in_progress: {
    text: 'Members with a transfer created or a prior authorisation submitted.',
    steps: ['Usually the largest share of the open pipeline.'],
    note: 'Most of the waiting in this whole process sits here, and most of it depends on the other pharmacy'
      + ' or the prescriber rather than on you.',
  },
};

const STUCK_REASON_TIPS = {
  'Warm Transfer': {
    text: 'Members waiting on a transfer from another pharmacy.',
    steps: ['Waiting on the other pharmacy rather than on your team.'],
    note: 'The reason most worth timing. Every day here is a day the member may run out, and section 09 is'
      + ' where that turnaround gets measured.',
  },
  'Text Promotion': {
    text: 'Members who came in from a text campaign and have not converted yet.',
    steps: ['Counted while the record is still open.'],
    note: 'A marketing response rather than a clinical block. Worth separating from the clinical reasons when'
      + ' deciding who to call.',
  },
  'Arine - Member Consent for Follow Up': {
    text: 'Members waiting to give consent for follow-up through Arine.',
    steps: ['Waiting on the member rather than the pharmacy.'],
    note: 'A single consent conversation clears the block.',
  },
  'Other Dept - Member Consent Follow Up': {
    text: 'Consent follow-ups sitting with another department.',
    steps: ['Owned outside this team, so nothing moves without chasing.'],
    note: 'Usually a small group, but these are the ones that go quiet — nobody in this team owns them.',
  },
  'Member Called SortPak Directly': {
    text: 'Members who got in touch themselves rather than being reached by outreach.',
    steps: ['Recorded when the member starts the conversation.'],
    note: 'The member has already made the first move, so these are normally the quickest to close. Worth'
      + ' working ahead of the records where you are still chasing a response.',
  },
};

const pipelineSteps = [
  <>
    Gap-days report shows no fill past
    {' '}
    <b>days supply + 7</b>
    {' '}
    → auto-flag (or staff flags manually) → record created as
    {' '}
    <b>identified</b>
    .
  </>,
  <>
    Staff calls the member or prescriber →
    {' '}
    <b>contacted</b>
    .
  </>,
  <>
    Action taken - transfer created, PA submitted →
    {' '}
    <b>in progress</b>
    , optionally linked to a
    {' '}
    <b>ScriptId</b>
    .
  </>,
  <>
    Member fills →
    {' '}
    <b>resolved</b>
    . No path forward →
    {' '}
    <b>lost</b>
    , with the reason kept.
  </>,
];

const WaitingRx = ({ section, params }) => {
  const firstLoad = useRef(true);

  const [pipelineData, setPipelineData] = useState({});
  const [loading, loadingToggle] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        loadingToggle(true);

        const { data } = await Api.getPipelineSummary(params);

        setPipelineData(data?.data || {});

        loadingToggle(false);
        firstLoad.current = false;
      } catch (e) {
        console.warn(e);
      }
    })();
  }, [params]);

  const cards = useMemo(() => {
    const stats = pipelineData?.kpis;

    return [
      {
        label: 'Open pipeline',
        value: formattedCount(stats?.openPipeline),
        tooltip: {
          text: 'Members currently somewhere in the transfer process.',
          steps: ['Anyone not yet marked resolved or lost.'],
          note: 'A live count, not a count for the date range. This is what is on the desk right now.',
        },
      },
      {
        label: 'Identified',
        value: formattedCount(stats?.identified),
        tooltip: {
          text: 'Members flagged as needing help, with no action taken yet.',
          steps: [
            'Created automatically when the gap report shows no fill past days supply plus seven days.',
            'Staff can also flag a member by hand.',
          ],
          note: 'A small number here means members are being picked up quickly once flagged — the queue is not'
            + ' backing up at the front.',
        },
      },
      {
        label: 'Contacted',
        value: formattedCount(stats?.contacted),
        tooltip: {
          text: 'Members someone has reached out to.',
          steps: ['Counted from the day a call or message goes out.'],
          note: 'This counts everyone ever contacted, while the stage chart below shows only those still'
            + ' waiting at that stage. The rest have already moved to in progress — the two answer different'
            + ' questions.',
          warn: true,
        },
      },
      {
        label: 'In progress',
        value: formattedCount(stats?.inProgress),
        tooltip: {
          text: 'Members whose transfer or prior authorisation is actively being worked.',
          steps: ['Moved here once something concrete has been done — a transfer created or a PA submitted.'],
          note: 'Usually the largest group, and mostly waiting on other people. Track how long they sit rather'
            + ' than trying to speed each one up.',
        },
      },
      {
        label: 'Resolved (30d)',
        value: formattedCount(stats?.resolved30d),
        tooltip: {
          text: 'Members who started filling again in the last 30 days.',
          steps: ['Closed as resolved once a fill comes through.'],
          note: 'Read against the lost count beside it — together they show how many of the members worked in'
            + ' the last month ended up back on therapy.',
        },
      },
      {
        label: 'Lost (30d)',
        value: formattedCount(stats?.lost30d),
        tooltip: {
          text: 'Members closed in the last 30 days with no way forward.',
          steps: ['Closed as lost, with the reason kept on the record.'],
          note: 'The reasons recorded here are what fills the churn breakdown in section 07. Closing without a'
            + ' reason is what leaves that chart empty.',
        },
      },
      {
        label: 'Avg days to resolve',
        value: stats?.avgDaysToResolve,
        tooltip: {
          text: 'How long a member takes to go from flagged to filling again.',
          steps: ['Averaged across members closed as resolved.'],
          note: 'Once it runs close to two weeks, a member on a 30-day supply will have run out before the'
            + ' transfer lands — a bridging supply is usually worth offering.',
        },
      },
      {
        label: 'Auto-flagged today',
        value: formattedCount(stats?.autoFlaggedToday),
        tooltip: {
          text: 'Members the system added to the pipeline today.',
          steps: [
            'Runs once a day against the gap report.',
            'Members already open in the pipeline are skipped.',
          ],
          note: 'Zero is normal on a day when nobody newly crossed the seven-day line. It does not mean the'
            + ' check did not run.',
        },
      },
    ];
  }, [pipelineData]);

  const { stages, outcomes, stuckReasons } = useMemo(() => {
    const stats = pipelineData?.kpis;

    const openPipeline = Number(stats?.openPipeline) || 0;

    return {
      stages: (pipelineData?.stages || []).map((d) => ({
        label: STAGE_LABELS[d.status] || d.status,
        value: d.count || 0,
        pct: openPipeline ? `${Math.round((d.count / openPipeline) * 100)}%` : '',
        tooltip: STAGE_TIPS[d.status],
      })),

      outcomes: [
        { label: 'Resolved - filling again (30d)', value: formattedCount(stats?.resolved30d) },
        { label: 'Lost - could not be helped (30d)', value: formattedCount(stats?.lost30d) },
      ],

      stuckReasons: (pipelineData?.stuckReasons || []).map((d) => ({
        label: d.stuckReason,
        value: d.count || 0,
        tooltip: STUCK_REASON_TIPS[d.stuckReason],
      })),
    };
  }, [pipelineData]);

  return (
    <>
      <SectionHead section={section} />

      <StatsCards
        firstLoad={firstLoad.current}
        loading={loading}
        cards={cards}
      />

      <div className={`${classes.row} ${classes.row_3}`}>
        <Card
          title="Pipeline stages"
          caption="Open members by stage · MemberPipelines.status"
          loading={loading}
          firstLoad={firstLoad.current}
          isEmpty={!stages.length}
        >
          <BarChart
            data={stages}
            className={classes.bordered}
          />

          <div className={classes.kv_list}>
            {outcomes.map((item) => (
              <div className={classes.kv} key={item.label}>
                {item.label}

                <span className={classes.kv_value}>{item.value}</span>
              </div>
            ))}
          </div>
        </Card>

        <Card
          title="Why the member is stuck"
          caption="Open pipeline by the reason kept on the record"
          loading={loading}
          firstLoad={firstLoad.current}
          isEmpty={!stuckReasons.length}
        >
          <LineChart data={stuckReasons} />
        </Card>

        <Card
          title="Where a status is set"
          caption="Two separate layers - they do not overlap"
        >
          <div className={classes.bordered}>
            <div className={classes.layer}>
              <p className={classes.layer_title}>Scripts Status</p>

              <p className={classes.layer_text}>
                What happened to a
                {' '}
                <b>prescription</b>
                {' '}
                - the normal fill workflow.
              </p>

              <div className={classes.layer_flow}>
                <Chip text="pending" />

                <span className={classes.layer_arrow}>→</span>

                <Chip text="processed" />

                <span className={classes.layer_arrow}>→</span>

                <Chip text="shipped" />

                <span className={classes.layer_arrow}>→</span>

                <Chip text="delivered" />
              </div>
            </div>

            <div className={classes.layer}>
              <p className={classes.layer_title}>Member Pipelines Status</p>

              <p className={classes.layer_text}>
                Why a
                {' '}
                <b>member</b>
                {' '}
                is not getting filled, and what is being done about it.
              </p>

              <div className={classes.layer_flow}>
                <span className={`${classes.pill} ${classes.pill_identified}`}>identified</span>

                <span className={classes.layer_arrow}>→</span>

                <span className={`${classes.pill} ${classes.pill_contacted}`}>contacted</span>

                <span className={classes.layer_arrow}>→</span>

                <span className={`${classes.pill} ${classes.pill_progress}`}>in progress</span>

                <span className={classes.layer_arrow}>→</span>

                <span className={`${classes.pill} ${classes.pill_resolved}`}>resolved</span>

                <span className={classes.layer_arrow}>/</span>

                <span className={`${classes.pill} ${classes.pill_lost}`}>lost</span>
              </div>
            </div>
          </div>

          <Steps items={pipelineSteps} />
        </Card>
      </div>

      <WaitingRxBoardCard params={params} />
    </>
  );
};

WaitingRx.propTypes = {
  section: PropTypes.object.isRequired,
  params: PropTypes.object.isRequired,
};

export default WaitingRx;
